import React, { useState, useCallback, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { NotebookEditor, NotebookCell } from './NotebookEditor';
import { getNotebookService } from '../../services/NotebookService';
import type NotebookService from '../../services/NotebookService';

interface CellManagerProps {
  sessionId: string;
  token: string;
  initialCells?: NotebookCell[];
  isReadOnly?: boolean;
  onSave?: (cells: NotebookCell[]) => void;
  onSaveAs?: (cells: NotebookCell[]) => void;
  onCloudSave?: () => void;
  cloudSaveStatus?: 'idle' | 'saving' | 'success' | 'error';
  className?: string;
  /** Callback to expose the addCell function externally (for AI code insertion) */
  onAddCellRef?: (fn: (code: string, type: 'code' | 'markdown') => void) => void;
  /** Current notebook path — used to detect when a different notebook is loaded */
  notebookPath?: string | null;
  /** Kernel interrupt handler (from parent) */
  onInterruptKernel?: () => void;
  /** Kernel restart handler (from parent) */
  onRestartKernel?: () => void;
  /** Current kernel status */
  kernelStatus?: string;
  /** Callback to expose current cells for external consumers (e.g., Table of Contents) */
  onCellsChange?: (cells: NotebookCell[]) => void;
  /** Show keyboard shortcuts modal */
  onShowShortcuts?: () => void;
  /** Show command palette */
  onShowCommandPalette?: () => void;
  /** Fires after any cell finishes execution (for variable inspector refresh) */
  onExecutionComplete?: () => void;
  /** Proxy auth code for embedded app iframes (Streamlit, Bokeh, etc.) */
  proxyAuthCode?: string | null;
  /** Callback to fetch a fresh proxy auth code (60s TTL) */
  getProxyAuthCode?: () => Promise<string | null>;
  /** Fires when dirty state changes (for tab dirty indicators) */
  onDirtyChange?: (isDirty: boolean) => void;
}

/**
 * Manages multiple notebook cells with execution, adding, deleting, and reordering.
 */
export const CellManager: React.FC<CellManagerProps> = ({
  sessionId,
  token,
  initialCells = [],
  notebookPath,
  isReadOnly = false,
  onSave,
  onSaveAs,
  onCloudSave,
  cloudSaveStatus = 'idle',
  className = '',
  onAddCellRef,
  onInterruptKernel,
  onRestartKernel,
  kernelStatus: parentKernelStatus,
  onCellsChange,
  onShowShortcuts,
  onShowCommandPalette,
  onExecutionComplete,
  proxyAuthCode,
  getProxyAuthCode,
  onDirtyChange,
}) => {
  const [cells, setCells] = useState<NotebookCell[]>(() => 
    initialCells.length > 0 ? initialCells : [
      {
        id: uuidv4(),
        type: 'code',
        source: '# Welcome to PropAlgos Native Jupyter!\nimport numpy as np\nimport pandas as pd\nprint("Ready to analyze!")',
        outputs: [],
        execution_count: null,
      }
    ]
  );
  
  const [activeCellId, setActiveCellId] = useState<string | null>(cells[0]?.id || null);
  const [executingCells, setExecutingCells] = useState<Set<string>>(new Set());
  const [notebookService, setNotebookService] = useState<NotebookService | null>(null);
  const [clipboard, setClipboard] = useState<NotebookCell[]>([]);
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());
  const [globalExecutionCount, setGlobalExecutionCount] = useState<number>(0);
  const [executionTimes, setExecutionTimes] = useState<Record<string, number>>({});
  const [queuedCells, setQueuedCells] = useState<Set<string>>(new Set());
  const [focusCellId, setFocusCellId] = useState<string | null>(null);
  const executionQueueRef = useRef<Array<{ cellId: string; code?: string }>>([]);
  const isProcessingQueueRef = useRef(false);
  const isDirtyRef = useRef(false);
  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;
  const markDirty = useCallback(() => {
    if (!isDirtyRef.current) {
      isDirtyRef.current = true;
      onDirtyChangeRef.current?.(true);
    }
  }, []);
  const cellsRef = useRef(cells);
  const deletedCellsStack = useRef<Array<{ cell: NotebookCell; index: number }>>([]);
  const runAfterRestartRef = useRef(false);

  // Refs for stable callbacks — avoids stale closures when React.memo blocks re-renders
  const notebookServiceRef = useRef<NotebookService | null>(null);
  notebookServiceRef.current = notebookService;
  const globalExecutionCountRef = useRef(0);
  globalExecutionCountRef.current = globalExecutionCount;
  const onExecutionCompleteRef = useRef(onExecutionComplete);
  onExecutionCompleteRef.current = onExecutionComplete;

  // Keep cellsRef in sync so auto-save interval reads current cells without depending on them
  useEffect(() => {
    cellsRef.current = cells;
    onCellsChange?.(cells);
  }, [cells, onCellsChange]);

  // Update cells when a different notebook is loaded (keyed on notebookPath, not cell content)
  const prevNotebookPathRef = useRef<string | null | undefined>(notebookPath);
  useEffect(() => {
    if (notebookPath === prevNotebookPathRef.current) return;
    prevNotebookPathRef.current = notebookPath;

    if (initialCells && initialCells.length > 0) {
      setCells(initialCells);
      setActiveCellId(initialCells[0]?.id || null);

      // Set global execution count to the maximum execution count in the loaded notebook
      const maxExecCount = initialCells.reduce((max, cell) => {
        if (cell.type === 'code' && cell.execution_count !== null && cell.execution_count !== undefined) {
          return Math.max(max, cell.execution_count);
        }
        return max;
      }, 0);
      setGlobalExecutionCount(maxExecCount);
    }
  }, [notebookPath, initialCells]);

  // initialize notebook service
  useEffect(() => {
    const service = getNotebookService({ sessionId, token });
    setNotebookService(service);
    
    // initialize and start session
    const init = async () => {
      await service.initialize();
      await service.startSession();
    };
    init();
    
    return () => {
      // cleanup handled by service cache
    };
  }, [sessionId, token]);

  // auto-save cells periodically (only when dirty)
  useEffect(() => {
    if (!onSave || isReadOnly) return;

    const saveInterval = setInterval(() => {
      if (isDirtyRef.current) {
        isDirtyRef.current = false;
        onDirtyChangeRef.current?.(false);
        onSave(cellsRef.current);
      }
    }, 30000); // check every 30 seconds

    return () => clearInterval(saveInterval);
  }, [onSave, isReadOnly]);

  const executeCellImmediate = useCallback(async (cellId: string, code?: string) => {
    const service = notebookServiceRef.current;
    if (!service) return;

    const cell = cellsRef.current.find(c => c.id === cellId);
    if (!cell || cell.type !== 'code') return;

    const codeToExecute = code !== undefined ? code : cell.source;

    setQueuedCells(prev => { const next = new Set(prev); next.delete(cellId); return next; });
    setExecutingCells(prev => new Set(prev).add(cellId));
    const startTime = performance.now();

    const newExecutionCount = globalExecutionCountRef.current + 1;
    setGlobalExecutionCount(newExecutionCount);

    // Clear outputs and set execution count at start
    setCells(prev => prev.map(c =>
      c.id === cellId ? { ...c, outputs: [], execution_count: newExecutionCount } : c
    ));

    // Stream outputs with rAF batching to avoid render thrashing
    const pendingOutputs: any[] = [];
    let rafId = 0;

    const flushOutputs = () => {
      if (pendingOutputs.length === 0) return;
      const batch = pendingOutputs.splice(0);
      setCells(prev => prev.map(c =>
        c.id === cellId ? { ...c, outputs: [...(c.outputs || []), ...batch] } : c
      ));
    };

    const onOutput = (output: any) => {
      pendingOutputs.push(output);
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(flushOutputs);
    };

    try {
      await service.executeCode(codeToExecute, onOutput);
      cancelAnimationFrame(rafId);
      flushOutputs();
    } catch (error) {
      console.error('Execution error:', error);
      cancelAnimationFrame(rafId);
      flushOutputs();
    } finally {
      const elapsed = Math.round(performance.now() - startTime);
      setExecutionTimes(prev => ({ ...prev, [cellId]: elapsed }));
      setExecutingCells(prev => {
        const next = new Set(prev);
        next.delete(cellId);
        return next;
      });
      onExecutionCompleteRef.current?.();
    }
  }, []); // Stable — reads from refs

  // Queue-aware execution: only one cell executes at a time, others show as queued
  const processExecutionQueue = useCallback(async () => {
    if (isProcessingQueueRef.current) return;
    isProcessingQueueRef.current = true;
    while (executionQueueRef.current.length > 0) {
      const { cellId, code } = executionQueueRef.current.shift()!;
      await executeCellImmediate(cellId, code);
    }
    isProcessingQueueRef.current = false;
  }, [executeCellImmediate]);

  const executeCell = useCallback(async (cellId: string, code?: string) => {
    executionQueueRef.current.push({ cellId, code });
    setQueuedCells(prev => new Set(prev).add(cellId));
    processExecutionQueue();
  }, [processExecutionQueue]);

  const updateCellSource = useCallback((cellId: string, source: string) => {
    markDirty();
    setCells(prev => prev.map(c =>
      c.id === cellId ? { ...c, source } : c
    ));
  }, []);

  const addCell = useCallback((index: number, type: 'code' | 'markdown' = 'code') => {
    markDirty();
    const newCell: NotebookCell = {
      id: uuidv4(),
      type,
      source: '',
      outputs: [],
      execution_count: null,
    };

    setCells(prev => {
      const next = [...prev];
      next.splice(index, 0, newCell);
      return next;
    });

    setActiveCellId(newCell.id);
  }, []);

  /**
   * Add a cell with content at the end of the notebook.
   * Used by AI sidebar to insert generated code.
   */
  const addCellWithContent = useCallback((code: string, type: 'code' | 'markdown' = 'code') => {
    const newCell: NotebookCell = {
      id: uuidv4(),
      type,
      source: code,
      outputs: [],
      execution_count: null,
    };

    setCells(prev => [...prev, newCell]);
    setActiveCellId(newCell.id);

    // Scroll to the new cell after a brief delay to allow render
    setTimeout(() => {
      const cellElement = document.getElementById(`cell-${newCell.id}`);
      if (cellElement) {
        cellElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  }, []);

  // Expose addCellWithContent to parent component
  useEffect(() => {
    if (onAddCellRef) {
      onAddCellRef(addCellWithContent);
    }
  }, [onAddCellRef, addCellWithContent]);

  const deleteCell = useCallback((cellId: string) => {
    markDirty();
    setCells(prev => {
      if (prev.length <= 1) {
        return prev.map(c => c.id === cellId ? { ...c, source: '', outputs: [] } : c);
      }

      const index = prev.findIndex(c => c.id === cellId);
      const cell = prev[index];
      if (cell) {
        deletedCellsStack.current.push({ cell, index });
      }
      const next = prev.filter(c => c.id !== cellId);

      if (cellId === activeCellId && next.length > 0) {
        const newActiveIndex = Math.min(index, next.length - 1);
        setActiveCellId(next[newActiveIndex].id);
      }

      return next;
    });
  }, [activeCellId]);

  const undoDeleteCell = useCallback(() => {
    const entry = deletedCellsStack.current.pop();
    if (!entry) return;
    markDirty();
    setCells(prev => {
      const next = [...prev];
      const insertAt = Math.min(entry.index, next.length);
      next.splice(insertAt, 0, entry.cell);
      return next;
    });
    setActiveCellId(entry.cell.id);
  }, []);

  const runCellsAbove = useCallback(async (cellId: string) => {
    const idx = cellsRef.current.findIndex(c => c.id === cellId);
    for (let i = 0; i < idx; i++) {
      const c = cellsRef.current[i];
      if (c.type === 'code') await executeCell(c.id);
    }
  }, [executeCell]);

  const runCellsBelow = useCallback(async (cellId: string) => {
    const idx = cellsRef.current.findIndex(c => c.id === cellId);
    for (let i = idx; i < cellsRef.current.length; i++) {
      const c = cellsRef.current[i];
      if (c.type === 'code') await executeCell(c.id);
    }
  }, [executeCell]);

  const requestComplete = useCallback(async (code: string, cursorPos: number) => {
    const service = notebookServiceRef.current;
    if (!service) return null;
    try {
      return await service.requestComplete(code, cursorPos);
    } catch {
      return null;
    }
  }, []); // Stable — reads from ref

  const requestInspect = useCallback(async (code: string, cursorPos: number) => {
    const service = notebookServiceRef.current;
    if (!service) return null;
    try {
      return await service.requestInspect(code, cursorPos);
    } catch {
      return null;
    }
  }, []); // Stable — reads from ref

  const moveCell = useCallback((cellId: string, direction: 'up' | 'down') => {
    markDirty();
    setCells(prev => {
      const index = prev.findIndex(c => c.id === cellId);
      if (index === -1) return prev;
      
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= prev.length) return prev;
      
      const next = [...prev];
      [next[index], next[newIndex]] = [next[newIndex], next[index]];
      return next;
    });
  }, []);

  const changeCellType = useCallback((cellId: string, type: 'code' | 'markdown' | 'raw') => {
    markDirty();
    setCells(prev => prev.map(c =>
      c.id === cellId ? { ...c, type, outputs: [] } : c
    ));
  }, []);

  const navigateUp = useCallback((cellId: string) => {
    const currentIndex = cells.findIndex(c => c.id === cellId);
    if (currentIndex > 0) {
      const prevId = cells[currentIndex - 1].id;
      setActiveCellId(prevId);
      requestAnimationFrame(() => {
        document.getElementById(`cell-${prevId}`)?.scrollIntoView({
          behavior: 'smooth', block: 'nearest'
        });
      });
    }
  }, [cells]);

  const navigateDown = useCallback((cellId: string) => {
    const currentIndex = cells.findIndex(c => c.id === cellId);
    if (currentIndex < cells.length - 1) {
      const nextId = cells[currentIndex + 1].id;
      setActiveCellId(nextId);
      setFocusCellId(nextId);
      requestAnimationFrame(() => {
        document.getElementById(`cell-${nextId}`)?.scrollIntoView({
          behavior: 'smooth', block: 'nearest'
        });
      });
    }
  }, [cells]);

  // Clipboard operations
  const copyCells = useCallback(() => {
    const cellsToCopy = selectedCells.size > 0 
      ? cells.filter(c => selectedCells.has(c.id))
      : cells.filter(c => c.id === activeCellId);
    setClipboard(cellsToCopy);
  }, [cells, selectedCells, activeCellId]);

  const cutCells = useCallback(() => {
    copyCells();
    const cellsToRemove = selectedCells.size > 0 ? selectedCells : new Set([activeCellId]);
    setCells(prev => prev.filter(c => !cellsToRemove.has(c.id)));
    setSelectedCells(new Set());
  }, [copyCells, selectedCells, activeCellId]);

  const pasteCells = useCallback(() => {
    if (clipboard.length === 0) return;
    
    const activeIndex = cells.findIndex(c => c.id === activeCellId);
    const insertIndex = activeIndex >= 0 ? activeIndex + 1 : cells.length;
    
    // Create new cells with new IDs
    const newCells = clipboard.map(cell => ({
      ...cell,
      id: uuidv4(),
    }));
    
    setCells(prev => {
      const next = [...prev];
      next.splice(insertIndex, 0, ...newCells);
      return next;
    });
    
    // Select the first pasted cell
    if (newCells.length > 0) {
      setActiveCellId(newCells[0].id);
    }
  }, [clipboard, cells, activeCellId]);

  const splitCell = useCallback((cellId: string, cursorPos: number) => {
    markDirty();
    setCells(prev => {
      const idx = prev.findIndex(c => c.id === cellId);
      if (idx === -1) return prev;
      const cell = prev[idx];
      const before = cell.source.slice(0, cursorPos).replace(/\n$/, '');
      const after = cell.source.slice(cursorPos).replace(/^\n/, '');
      const newCell: NotebookCell = {
        id: uuidv4(),
        type: cell.type,
        source: after,
        outputs: [],
        execution_count: null,
      };
      const next = [...prev];
      next[idx] = { ...cell, source: before, outputs: [], execution_count: null };
      next.splice(idx + 1, 0, newCell);
      return next;
    });
  }, []);

  const runAllCells = useCallback(async () => {
    const codeCellIds = cellsRef.current.filter(c => c.type === 'code').map(c => c.id);
    for (const cellId of codeCellIds) {
      await executeCell(cellId);
    }
  }, [executeCell]);

  const restartAndRunAll = useCallback(() => {
    runAfterRestartRef.current = true;
    onRestartKernel?.();
  }, [onRestartKernel]);

  // When kernel transitions to idle after a restart-and-run-all, execute all cells
  useEffect(() => {
    if (runAfterRestartRef.current && parentKernelStatus === 'idle') {
      runAfterRestartRef.current = false;
      runAllCells();
    }
  }, [parentKernelStatus, runAllCells]);

  const clearAllOutputs = useCallback(() => {
    setCells(prev => prev.map(c => ({ ...c, outputs: [], execution_count: null })));
    setGlobalExecutionCount(0); // Reset global counter when clearing all outputs
  }, []);

  const clearCellOutput = useCallback((cellId: string) => {
    setCells(prev => prev.map(c =>
      c.id === cellId ? { ...c, outputs: [], execution_count: null } : c
    ));
  }, []);

  const applyFixToCell = useCallback((cellId: string, code: string) => {
    setCells(prev => prev.map(c =>
      c.id === cellId ? { ...c, source: code } : c
    ));
  }, []);

  const insertCellAboveWithContent = useCallback((cellId: string, code: string) => {
    markDirty();
    const newCell: NotebookCell = {
      id: uuidv4(),
      type: 'code',
      source: code,
      outputs: [],
      execution_count: null,
    };
    setCells(prev => {
      const idx = prev.findIndex(c => c.id === cellId);
      if (idx < 0) return [...prev, newCell];
      const next = [...prev];
      next.splice(idx, 0, newCell);
      return next;
    });
    setActiveCellId(newCell.id);
  }, []);

  const updateCellTags = useCallback((cellId: string, tags: string[]) => {
    markDirty();
    setCells(prev => prev.map(c =>
      c.id === cellId ? { ...c, metadata: { ...c.metadata, tags } } : c
    ));
  }, []);

  // keyboard navigation
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!activeCellId) return;
    
    const activeIndex = cells.findIndex(c => c.id === activeCellId);
    if (activeIndex === -1) return;
    
    // Check if we're in edit mode (CodeMirror or input is focused)
    const isEditing = document.activeElement?.closest('.cm-editor') || 
                     document.activeElement?.tagName === 'TEXTAREA' || 
                     document.activeElement?.tagName === 'INPUT';
    
    // Handle save shortcut (works in both modes)
    if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault();
      if (onSave && !isReadOnly) {
        onSave(cells);
      }
      return;
    }

    // Command palette (works in both modes)
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'p') {
      e.preventDefault();
      onShowCommandPalette?.();
      return;
    }

    // Only handle command mode shortcuts when not editing
    if (isEditing) return;
    
    switch(e.key) {
      case 'ArrowUp':
        if (e.shiftKey) {
          // Multi-select with Shift+Arrow
          const newSelection = new Set(selectedCells);
          if (activeIndex > 0) {
            newSelection.add(cells[activeIndex - 1].id);
            newSelection.add(activeCellId);
            setSelectedCells(newSelection);
            setActiveCellId(cells[activeIndex - 1].id);
          }
        } else {
          // Normal navigation
          if (activeIndex > 0) {
            setActiveCellId(cells[activeIndex - 1].id);
            setSelectedCells(new Set());
          }
        }
        e.preventDefault();
        break;
        
      case 'ArrowDown':
        if (e.shiftKey) {
          // Multi-select with Shift+Arrow
          const newSelection = new Set(selectedCells);
          if (activeIndex < cells.length - 1) {
            newSelection.add(cells[activeIndex + 1].id);
            newSelection.add(activeCellId);
            setSelectedCells(newSelection);
            setActiveCellId(cells[activeIndex + 1].id);
          }
        } else {
          // Normal navigation
          if (activeIndex < cells.length - 1) {
            setActiveCellId(cells[activeIndex + 1].id);
            setSelectedCells(new Set());
          }
        }
        e.preventDefault();
        break;
        
      case 'x':
        if (!e.shiftKey && !e.ctrlKey && !e.metaKey) {
          cutCells();
          e.preventDefault();
        }
        break;
        
      case 'c':
        if (!e.shiftKey && !e.ctrlKey && !e.metaKey) {
          copyCells();
          e.preventDefault();
        }
        break;
        
      case 'v':
        if (!e.shiftKey && !e.ctrlKey && !e.metaKey) {
          pasteCells();
          e.preventDefault();
        }
        break;

      case 'z':
        if (!e.shiftKey && !e.ctrlKey && !e.metaKey) {
          undoDeleteCell();
          e.preventDefault();
        }
        break;

      case 'h':
        if (!e.shiftKey && !e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          onShowShortcuts?.();
        }
        break;

      case 'M':
        if (e.shiftKey) {
          // Merge selected cells
          const cellsToMerge = selectedCells.size > 0 
            ? cells.filter(c => selectedCells.has(c.id))
            : [cells[activeIndex], cells[activeIndex + 1]].filter(Boolean);
          
          if (cellsToMerge.length > 1) {
            const mergedSource = cellsToMerge.map(c => c.source).join('\n');
            const firstCell = cellsToMerge[0];
            
            setCells(prev => prev.map(c => {
              if (c.id === firstCell.id) {
                return { ...c, source: mergedSource };
              }
              if (cellsToMerge.slice(1).some(mc => mc.id === c.id)) {
                return null;
              }
              return c;
            }).filter(Boolean) as NotebookCell[]);
            
            setSelectedCells(new Set());
            setActiveCellId(firstCell.id);
          }
          e.preventDefault();
        }
        break;
    }
  }, [activeCellId, cells, selectedCells, onSave, isReadOnly, cutCells, copyCells, pasteCells, undoDeleteCell, onShowShortcuts, onShowCommandPalette]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div className={`p-6 ${className}`}>
      {/* toolbar with glassy sharp style */}
      <div className="mb-4 flex items-center space-x-2 bg-black/50 border border-gray-800/50 backdrop-blur-sm p-2 shadow-lg">
        <button
          onClick={runAllCells}
          className="px-4 py-1.5 text-sm bg-gray-700/50 text-white hover:bg-gray-700/80 backdrop-blur-sm transition-all border border-gray-600/30"
        >
          ▶ Run All
        </button>
        <button
          onClick={clearAllOutputs}
          className="px-4 py-1.5 text-sm bg-gray-700/50 text-white hover:bg-gray-700/80 backdrop-blur-sm transition-all border border-gray-600/30"
        >
          Clear Outputs
        </button>
        {onInterruptKernel && (
          <button
            onClick={onInterruptKernel}
            disabled={parentKernelStatus !== 'busy'}
            className="px-3 py-1.5 text-sm bg-gray-700/50 text-white hover:bg-yellow-700/80 hover:text-yellow-200 disabled:opacity-30 disabled:cursor-not-allowed backdrop-blur-sm transition-all border border-gray-600/30 flex items-center gap-1.5"
            title="Interrupt Kernel"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <rect x="6" y="6" width="12" height="12" strokeWidth={2} strokeLinecap="round" />
            </svg>
            Interrupt
          </button>
        )}
        {onRestartKernel && (
          <>
            <button
              onClick={onRestartKernel}
              disabled={parentKernelStatus === 'restarting'}
              className="px-3 py-1.5 text-sm bg-gray-700/50 text-white hover:bg-red-700/80 hover:text-red-200 disabled:opacity-30 disabled:cursor-not-allowed backdrop-blur-sm transition-all border border-gray-600/30 flex items-center gap-1.5"
              title="Restart Kernel"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Restart
            </button>
            <button
              onClick={restartAndRunAll}
              disabled={parentKernelStatus === 'restarting'}
              className="px-3 py-1.5 text-sm bg-gray-700/50 text-white hover:bg-orange-700/80 hover:text-orange-200 disabled:opacity-30 disabled:cursor-not-allowed backdrop-blur-sm transition-all border border-gray-600/30 flex items-center gap-1.5"
              title="Restart Kernel & Run All Cells"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Restart &amp; Run</span>
            </button>
          </>
        )}
        <button
          onClick={() => {
            const activeIndex = cells.findIndex(c => c.id === activeCellId);
            addCell(activeIndex >= 0 ? activeIndex + 1 : cells.length, 'code');
          }}
          className="px-4 py-1.5 text-sm bg-gray-700/50 text-white hover:bg-gray-700/80 backdrop-blur-sm transition-all border border-gray-600/30"
          title="Add Code Cell Below (B key in command mode)"
        >
          + Code
        </button>
        <button
          onClick={() => {
            const activeIndex = cells.findIndex(c => c.id === activeCellId);
            addCell(activeIndex >= 0 ? activeIndex + 1 : cells.length, 'markdown');
          }}
          className="px-4 py-1.5 text-sm bg-gray-700/50 text-white hover:bg-gray-700/80 backdrop-blur-sm transition-all border border-gray-600/30"
          title="Add Markdown Cell Below"
        >
          + Markdown
        </button>
        <div className="ml-auto flex items-center space-x-2">
          {onSave && (
            <button
              onClick={() => onSave(cells)}
              className="px-4 py-1.5 text-sm bg-green-600/80 text-white hover:bg-green-600 backdrop-blur-sm transition-all shadow hover:shadow-green-500/20"
              title="Save to instance (Cmd+S)"
            >
              Save
            </button>
          )}
          {onSaveAs && (
            <button
              onClick={() => onSaveAs(cells)}
              className="px-4 py-1.5 text-sm bg-gray-700/50 text-white hover:bg-gray-700/80 backdrop-blur-sm transition-all border border-gray-600/30"
              title="Save to a new path"
            >
              Save As
            </button>
          )}
          {onCloudSave && (
            <button
              onClick={onCloudSave}
              disabled={cloudSaveStatus === 'saving'}
              className={`px-4 py-1.5 text-sm backdrop-blur-sm transition-all border flex items-center space-x-1.5 ${
                cloudSaveStatus === 'saving'
                  ? 'bg-blue-800/50 text-blue-300 border-blue-700/50 cursor-wait'
                  : 'bg-blue-600/30 text-blue-300 hover:bg-blue-600/50 hover:text-white border-blue-600/30 shadow hover:shadow-blue-500/20'
              }`}
              title="Save to S3 cloud storage"
            >
              {cloudSaveStatus === 'saving' ? (
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.25"/>
                  <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              )}
              <span>{cloudSaveStatus === 'saving' ? 'Saving...' : 'Cloud'}</span>
            </button>
          )}
        </div>
      </div>

      {/* cells */}
      <div className="space-y-0">
        {cells.map((cell, index) => (
          <React.Fragment key={cell.id}>
            {/* Between-cell add bar */}
            <div className="group/add relative h-6 z-10 flex items-center justify-center">
              <div className="absolute inset-x-6 top-1/2 h-px bg-gray-700/40 group-hover/add:bg-blue-500/40 transition-colors" />
              <button
                onClick={() => addCell(index, 'code')}
                className="relative px-3 py-0.5 text-[11px] text-gray-500 hover:text-white bg-gray-900/80 hover:bg-blue-600/80 border border-gray-700/50 hover:border-blue-500/60 rounded-full backdrop-blur-sm transition-all flex items-center gap-1"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
            <div
              id={`cell-${cell.id}`}
              className={selectedCells.has(cell.id) ? 'opacity-80' : ''}
              style={
                // content-visibility: auto skips rendering off-screen cells.
                // Active/executing cells are excluded to prevent layout jank.
                cell.id !== activeCellId && !executingCells.has(cell.id)
                  ? { contentVisibility: 'auto' as any, containIntrinsicSize: 'auto 120px' }
                  : undefined
              }
            >
            <NotebookEditor
              cell={cell}
              isActive={cell.id === activeCellId}
              isExecuting={executingCells.has(cell.id)}
              onExecute={(code) => executeCell(cell.id, code)}
              onChange={(source) => updateCellSource(cell.id, source)}
              onFocus={() => {
                setActiveCellId(cell.id);
                if (!selectedCells.has(cell.id)) {
                  setSelectedCells(new Set());
                }
              }}
              onDelete={() => deleteCell(cell.id)}
              onAddCellAbove={() => addCell(index, 'code')}
              onAddCellBelow={() => addCell(index + 1, 'code')}
              onMoveUp={() => moveCell(cell.id, 'up')}
              onMoveDown={() => moveCell(cell.id, 'down')}
              onChangeCellType={(type) => changeCellType(cell.id, type)}
              onNavigateUp={() => navigateUp(cell.id)}
              onNavigateDown={() => navigateDown(cell.id)}
              sessionId={sessionId}
              onApplyFix={(code) => applyFixToCell(cell.id, code)}
              onInsertCellAbove={(code) => insertCellAboveWithContent(cell.id, code)}
              onApplyCodeSuggestion={(code) => applyFixToCell(cell.id, code)}
              onClearOutput={() => clearCellOutput(cell.id)}
              executionTimeMs={executionTimes[cell.id]}
              requestComplete={requestComplete}
              requestInspect={requestInspect}
              onRunAbove={() => runCellsAbove(cell.id)}
              onRunBelow={() => runCellsBelow(cell.id)}
              onSplitCell={(cursorPos) => splitCell(cell.id, cursorPos)}
              isQueued={queuedCells.has(cell.id)}
              onUpdateTags={(tags) => updateCellTags(cell.id, tags)}
              proxyAuthCode={proxyAuthCode}
              getProxyAuthCode={getProxyAuthCode}
              shouldFocusEditor={focusCellId === cell.id}
              onFocusHandled={() => { if (focusCellId === cell.id) setFocusCellId(null); }}
            />
          </div>
          </React.Fragment>
        ))}
      </div>

      {/* add cell button at bottom */}
      <div className="mt-4 flex justify-center">
        <button
          onClick={() => addCell(cells.length, 'code')}
          className="px-6 py-2 text-sm text-gray-400 hover:text-white bg-black/30 hover:bg-black/50 border border-gray-800/50 backdrop-blur-sm transition-all shadow hover:shadow-lg"
        >
          + Add Cell
        </button>
      </div>
    </div>
  );
};